"use client";

import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import type { IVideoPlayerRef } from "@/component/VideoPlayer";
import { useSpacePlayback } from "@/hooks/useSpacePlayback";
import { downloadFile } from "@/util/file";
import { buildDownloadFilename } from "@/util/filename";
import classNames from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaEditorContext } from "../../context";
import { GeneratingVersionItem } from "../shared/GeneratingIcon";
import { MediaPreviewContent } from "./components/MediaPreviewContent";
import { MediaVersionList } from "./components/MediaVersionList";
import { MediaPreviewProvider } from "./context";
import { getReplacementAcceptMime, isVideoLikeMedia } from "./utils";

export interface MediaPreviewPanelProps {
  className?: string;
  isActive?: boolean;
}

export const MediaPreviewPanel = memo(function MediaPreviewPanel(
  props: MediaPreviewPanelProps
) {
  const { className, isActive = false } = props;
  const {
    data: { item },
    navigation: { index },
    config: {
      canEdit = true,
      isOwner: showDownload = true,
      threadId,
      threadTitle,
      title,
    },
    status: { replacing = false },
    actions: {
      onSelectVersion,
      onReplace,
      onRemix,
      onPreviewContentHeightChange,
    },
  } = useMediaEditorContext();

  const current = item.current;
  const versions = item.versions;
  const mediaType = item.mediaType;

  const isVideoMedia = isVideoLikeMedia(mediaType);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewContentRef = useRef<HTMLDivElement>(null);
  const videoPlayerRef = useRef<IVideoPlayerRef | null>(null);

  // 初始化 Embla Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      skipSnaps: true,
      containScroll: "trimSnaps",
    },
    [WheelGesturesPlugin()]
  );

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const rafIdRef = useRef<number | null>(null);

  const updateEdgeFlags = useCallback(() => {
    if (!emblaApi) return;
    setAtStart(!emblaApi.canScrollPrev());
    setAtEnd(!emblaApi.canScrollNext());
  }, [emblaApi]);

  const handleScroll = useCallback(() => {
    if (!emblaApi) return;
    if (rafIdRef.current != null) return;
    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = null;
      updateEdgeFlags();
    });
  }, [emblaApi, updateEdgeFlags]);

  // 监听滚动事件并节流（rAF），在初始化与重新初始化时更新边界状态
  useEffect(() => {
    if (!emblaApi) return;
    updateEdgeFlags();
    emblaApi.on("scroll", handleScroll);
    emblaApi.on("reInit", updateEdgeFlags);
    return () => {
      emblaApi.off?.("scroll", handleScroll);
      emblaApi.off?.("reInit", updateEdgeFlags);
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [emblaApi, handleScroll, updateEdgeFlags]);

  // 根据 mediaType 决定接受的文件类型
  const getAcceptType = () => getReplacementAcceptMime(mediaType);

  // 处理文件选择
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) return;
    const file = event.target.files?.[0];
    if (!file) return;

    // 直接将 File 对象传给 onReplace 回调
    onReplace(file);

    // 清空 input 值，允许重复选择同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleReplaceClick = useCallback(() => {
    if (!canEdit) return;
    fileInputRef.current?.click();
  }, [canEdit]);

  const handleRemixClick = useCallback(() => {
    if (!canEdit) return;
    onRemix();
  }, [canEdit, onRemix]);

  const handleDownload = useCallback(async () => {
    if (!current?.src) {
      return;
    }

    logger.event(VidMuseEvent.MediaEditorModalDownloadBtn, {
      thread_id: threadId,
      media_type: item.mediaType,
      current_index: index,
    });

    return downloadFile(
      current.src,
      buildDownloadFilename(threadTitle, title, current.src)
    );
  }, [current, item, threadId, index, threadTitle, title]);

  const handleSpaceToggle = useCallback(() => {
    const player = videoPlayerRef.current;
    const video = player?.getVideoElement();
    if (!video) return false;

    if (video.paused) {
      player?.play();
    } else {
      player?.pause();
    }
    return true;
  }, []);

  useSpacePlayback(handleSpaceToggle, {
    priority: 10,
    enabled: isActive && isVideoMedia,
  });

  const previewContextValue = useMemo(
    () => ({
      mediaType,
      current,
      canEdit,
      showDownload,
      replacing,
      onRemix: handleRemixClick,
      onDownload: handleDownload,
      onReplace: handleReplaceClick,
    }),
    [
      mediaType,
      current,
      canEdit,
      showDownload,
      replacing,
      handleRemixClick,
      handleDownload,
      handleReplaceClick,
    ]
  );

  // 监听 media-preview-content 的高度变化
  useEffect(() => {
    if (!previewContentRef.current || !onPreviewContentHeightChange) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.target.getBoundingClientRect().height;
      if (height) {
        onPreviewContentHeightChange(height);
      }
    });

    resizeObserver.observe(previewContentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [onPreviewContentHeightChange]);

  if (!current) {
    return null;
  }

  return (
    <div
      className={classNames(
        "flex flex-col gap-5 lg:w-[calc(100%-400px)]",
        className
      )}
    >
      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptType()}
        onChange={handleFileChange}
        disabled={!canEdit}
        style={{ display: "none" }}
      />

      {/* 媒体预览容器 */}
      <div
        ref={previewContentRef}
        className={`relative flex items-center justify-center w-full aspect-video overflow-hidden rounded-2xl`}
      >
        <MediaPreviewProvider value={previewContextValue}>
          {current.status !== "success" ? (
            <GeneratingVersionItem
              className="w-full h-full"
              size="large"
              isError={current.status === "failed"}
            />
          ) : (
            <MediaPreviewContent videoPlayerRef={videoPlayerRef} />
          )}
        </MediaPreviewProvider>
      </div>

      <MediaVersionList
        versions={versions}
        current={current}
        isVideoMedia={isVideoMedia}
        onSelectVersion={onSelectVersion}
        scrollContainerRef={emblaRef}
        showHeadGradient={!atStart}
        showTailGradient={!atEnd}
      />
    </div>
  );
});
